import { useRef, useEffect, useCallback } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

/* ── Dual-column time selector (hour | minute) ────────── */

function TimeColumns({
  value,
  onChange,
}: {
  value: Date | null;
  onChange: (date: Date) => void;
}) {
  const hour = value?.getHours() ?? 0;
  const minute = value?.getMinutes() ?? 0;
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  const scrollTo = useCallback((ref: React.RefObject<HTMLDivElement | null>, idx: number) => {
    const el = ref.current;
    if (!el) return;
    const item = el.children[idx] as HTMLElement | undefined;
    if (item) el.scrollTop = item.offsetTop - el.offsetHeight / 2 + item.offsetHeight / 2;
  }, []);

  useEffect(() => { scrollTo(hourRef, hour); }, [hour, scrollTo]);
  useEffect(() => { scrollTo(minuteRef, minute); }, [minute, scrollTo]);

  function setTime(h: number, m: number) {
    const d = value ? new Date(value) : new Date();
    d.setHours(h, m, 0, 0);
    onChange(d);
  }

  const itemBase =
    'flex items-center justify-center h-8 min-w-[44px] text-sm cursor-pointer rounded transition-colors';
  const itemNormal =
    'text-gray-700 hover:bg-blue-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-blue-900/30 dark:hover:text-blue-400';
  const itemSelected =
    'bg-blue-600 text-white font-semibold';

  return (
    <div className="woli-time-columns flex border-l border-gray-200 dark:border-gray-700">
      {/* Hours */}
      <div
        ref={hourRef}
        className="flex flex-col overflow-y-auto py-1 px-0.5"
        style={{ height: 220, scrollbarWidth: 'thin' }}
      >
        {Array.from({ length: 24 }, (_, h) => (
          <button
            key={h}
            type="button"
            onClick={() => setTime(h, minute)}
            className={`${itemBase} ${h === hour ? itemSelected : itemNormal}`}
          >
            {String(h).padStart(2, '0')}
          </button>
        ))}
      </div>
      {/* Minutes */}
      <div
        ref={minuteRef}
        className="flex flex-col overflow-y-auto py-1 px-0.5 border-l border-gray-100 dark:border-gray-700"
        style={{ height: 220, scrollbarWidth: 'thin' }}
      >
        {Array.from({ length: 60 }, (_, m) => (
          <button
            key={m}
            type="button"
            onClick={() => setTime(hour, m)}
            className={`${itemBase} ${m === minute ? itemSelected : itemNormal}`}
          >
            {String(m).padStart(2, '0')}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Custom calendar container ────────────────────────── */

function CalendarWithTime({
  children,
  value,
  onTimeChange,
  showTime,
}: {
  children: React.ReactNode;
  value: Date | null;
  onTimeChange: (d: Date) => void;
  showTime: boolean;
}) {
  return (
    <div className="woli-calendar-container flex">
      <div>{children}</div>
      {showTime && <TimeColumns value={value} onChange={onTimeChange} />}
    </div>
  );
}

/* ── Props ────────────────────────────────────────────── */

interface Props {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  showTimeSelect?: boolean;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  isClearable?: boolean;
  disabled?: boolean;
  className?: string;
}

/* ── DateTimePicker ──────────────────────────────────── */

export function DateTimePicker({
  value,
  onChange,
  placeholder = 'dd/mm/aaaa --:--',
  showTimeSelect = true,
  dateFormat = 'dd/MM/yyyy HH:mm',
  minDate,
  maxDate,
  isClearable = true,
  disabled = false,
  className = '',
}: Props) {
  function handleTimeChange(d: Date) {
    onChange(d);
  }

  return (
    <DatePicker
      selected={value}
      onChange={onChange}
      locale="pt-BR"
      showTimeSelect={false}
      dateFormat={dateFormat}
      placeholderText={placeholder}
      minDate={minDate}
      maxDate={maxDate}
      isClearable={isClearable}
      disabled={disabled}
      todayButton="Hoje"
      showPopperArrow={false}
      autoComplete="off"
      className={`w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 ${disabled ? 'cursor-not-allowed !bg-gray-100 dark:!bg-gray-700' : ''} ${className}`}
      calendarClassName="woli-datepicker"
      wrapperClassName="w-full"
      calendarContainer={({ children }) => (
        <CalendarWithTime
          value={value}
          onTimeChange={handleTimeChange}
          showTime={showTimeSelect}
        >
          {children}
        </CalendarWithTime>
      )}
    />
  );
}

/* ── DateOnlyPicker ──────────────────────────────────── */

export function DateOnlyPicker({
  value,
  onChange,
  placeholder = 'dd/mm/aaaa',
  minDate,
  maxDate,
  isClearable = true,
  disabled = false,
  className = '',
}: Omit<Props, 'showTimeSelect' | 'dateFormat'>) {
  return (
    <DateTimePicker
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      showTimeSelect={false}
      dateFormat="dd/MM/yyyy"
      minDate={minDate}
      maxDate={maxDate}
      isClearable={isClearable}
      disabled={disabled}
      className={className}
    />
  );
}
